'use client';

import { createContext, useContext, useState, useCallback } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: ToastAction;
}

interface ToastOptions {
  duration?: number;
  action?: ToastAction;
}

/**
 * Every show returns the toast's id so a caller can retract it before its
 * timeout - the auth jam toast is dismissed the moment the lock is stolen
 * back and the client works again (2026-09-17).
 */
interface ToastContextType {
  showToast: (type: ToastType, message: string, options?: ToastOptions) => string;
  success: (message: string, options?: ToastOptions) => string;
  error: (message: string, options?: ToastOptions) => string;
  warning: (message: string, options?: ToastOptions) => string;
  info: (message: string, options?: ToastOptions) => string;
  /** Retract a toast early by the id its show call returned. */
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);
// Two at most: on a 320 px phone three stacked toasts filled a third of the screen.
const MAX_TOASTS = 2;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [exitingToasts, setExitingToasts] = useState<Set<string>>(new Set());

  const removeToastWithAnimation = useCallback((id: string) => {
    // Start exit animation
    setExitingToasts(prev => new Set(prev).add(id));

    // Remove after animation completes (300ms)
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      setExitingToasts(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);
  }, []);

  const showToast = useCallback((type: ToastType, message: string, options?: ToastOptions) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: Toast = { id, type, message, duration: options?.duration, action: options?.action };

    setToasts(prev => [...prev.slice(-(MAX_TOASTS - 1)), newToast]);

    // Auto-dismiss all toasts (including errors)
    // Toasts with actions stay longer by default
    const baseTimeout = options?.duration ?? (
      type === 'success' ? 4000 :
      type === 'info' ? 5000 :
      type === 'warning' ? 6000 :
      8000 // errors get 8 seconds
    );
    // Add extra time if there's an action button
    const timeout = options?.action ? Math.max(baseTimeout, 8000) : baseTimeout;

    setTimeout(() => {
      removeToastWithAnimation(id);
    }, timeout);

    return id;
  }, [removeToastWithAnimation]);

  const success = useCallback((message: string, options?: ToastOptions) =>
    showToast('success', message, options), [showToast]);

  const error = useCallback((message: string, options?: ToastOptions) =>
    showToast('error', message, options), [showToast]);

  const warning = useCallback((message: string, options?: ToastOptions) =>
    showToast('warning', message, options), [showToast]);

  const info = useCallback((message: string, options?: ToastOptions) =>
    showToast('info', message, options), [showToast]);

  const dismiss = useCallback((id: string) => {
    removeToastWithAnimation(id);
  }, [removeToastWithAnimation]);

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success': return <CheckCircle className="h-5 w-5" />;
      case 'error': return <XCircle className="h-5 w-5" />;
      case 'warning': return <AlertTriangle className="h-5 w-5" />;
      case 'info': return <Info className="h-5 w-5" />;
    }
  };

  const getStyles = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-white border-l-4 border-green-600 text-gray-900 shadow-lg';
      case 'error':
        return 'bg-white border-l-4 border-red-600 text-gray-900 shadow-lg';
      case 'warning':
        return 'bg-white border-l-4 border-amber-500 text-gray-900 shadow-lg';
      case 'info':
        return 'bg-white border-l-4 border-blue-600 text-gray-900 shadow-lg';
    }
  };

  const getIconColor = (type: ToastType) => {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-amber-500';
      case 'info': return 'text-blue-600';
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info, dismiss }}>
      {children}

      {/* Toast Container: bottom centre on every screen, above a bottom bar when one is
          showing (--bottom-bar-h, see useBottomBarOffset). At the top they covered the
          header and page title on a phone. */}
      <div
        className="fixed inset-x-4 z-[10000] mx-auto flex max-w-md flex-col gap-3 pointer-events-none"
        style={{ bottom: 'calc(1rem + var(--bottom-bar-h, 0px) + env(safe-area-inset-bottom, 0px))' }}
      >
        {toasts.map(toast => (
          <div
            key={toast.id}
            role="status"
            aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
            aria-atomic="true"
            className={`${getStyles(toast.type)} rounded-lg px-4 py-3.5 flex items-start gap-3 pointer-events-auto
              transition-all duration-300 ease-out ${
                exitingToasts.has(toast.id)
                  ? 'opacity-0 translate-y-3'
                  : 'animate-in slide-in-from-bottom-3 fade-in'
              }`}
          >
            <div className={`flex-shrink-0 ${getIconColor(toast.type)}`}>
              {getIcon(toast.type)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-relaxed break-words">
                {toast.message}
              </p>
              {toast.action && (
                <div className="mt-2">
                  {toast.action.href ? (
                    <Link
                      href={toast.action.href}
                      className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2"
                      onClick={() => dismiss(toast.id)}
                    >
                      {toast.action.label} →
                    </Link>
                  ) : toast.action.onClick ? (
                    <Button
                      variant="link"
                      onClick={() => {
                        toast.action?.onClick?.();
                        dismiss(toast.id);
                      }}
                      className="h-auto p-0 text-sm font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2"
                    >
                      {toast.action.label} →
                    </Button>
                  ) : null}
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              onClick={() => dismiss(toast.id)}
              className="flex-shrink-0 h-auto p-1 text-gray-400 hover:text-gray-600 hover:bg-transparent"
              aria-label={`Dismiss ${toast.type} notification: ${toast.message}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}
